import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaBus,
  FaClock,
  FaEnvelope,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaPhone,
  FaRoute,
  FaStar,
  FaTicketAlt,
  FaUserTie,
} from "react-icons/fa";
import NavBar from "../components/nav";
import Footer from "./footer";
import VisitorTips from "./VisitorTips";
import "../css/busDescription.css";
import { notifyError, notifySuccess } from "../utils/notify";

const formatMoney = (value) => {
  const numericValue = Number(value) || 0;

  if (!numericValue) {
    return "Contact for fare";
  }

  return `R ${numericValue.toLocaleString("en-ZA", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const normaliseList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

const formatScheduleItem = (item) => {
  if (typeof item === "string") return item;
  if (!item || typeof item !== "object") return "";

  return [
    item.day,
    item.route,
    item.departure || item.departureTime || item.time,
    item.arrival || item.arrivalTime,
  ]
    .filter(Boolean)
    .join(" - ");
};

const BusDescription = () => {
  const navigate = useNavigate();
  const { state = {} } = useLocation();
  const [selectedImage, setSelectedImage] = useState("");

  const {
    title = "Bus Service",
    description = "No description available.",
    image = "",
    gallery: galleryState = [],
    location: busLocation = "Main terminal not specified",
    rating = 0,
    routes: routeState = [],
    schedule: scheduleState = [],
    phone = "",
    email = "",
    price = 0,
    operator = "Operator not specified",
    mapLink = "",
  } = state;

  const gallery = useMemo(() => {
    const images = normaliseList(galleryState);
    return [...new Set([image, ...images].filter(Boolean))];
  }, [galleryState, image]);

  const routes = normaliseList(routeState);
  const schedule =
    scheduleState && !Array.isArray(scheduleState) && typeof scheduleState === "object"
      ? Object.entries(scheduleState).map(([day, hours]) => `${day}: ${hours}`)
      : normaliseList(scheduleState).map(formatScheduleItem).filter(Boolean);
  const safeRating = Math.min(5, Math.max(0, Number(rating) || 0));
  const fareLabel = formatMoney(price);
  const directionsLink =
    mapLink ||
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${title} ${busLocation}`
    )}`;

  const openDirections = () => {
    const hasLocation =
      mapLink ||
      (busLocation && busLocation !== "Main terminal not specified");

    if (!hasLocation) {
      notifyError("Map directions are not available for this transport service yet.");
      return;
    }

    notifySuccess("Opening Maps for trip directions.");
    window.open(directionsLink, "_blank", "noopener,noreferrer");
  };

  const callOperator = () => {
    if (!phone) {
      notifyError("A phone number has not been added for this operator yet.");
      return;
    }

    window.location.href = `tel:${phone}`;
  };

  const emailOperator = () => {
    if (!email) {
      notifyError("An email address has not been added for this operator yet.");
      return;
    }

    window.location.href = `mailto:${email}`;
  };

  useEffect(() => {
    setSelectedImage(gallery[0] || "");
  }, [gallery]);

  return (
    <div className="bus-description-page">
      <NavBar />

      <main className="bus-detail-container">
        <header className="bus-title-row">
          <button
            type="button"
            className="bus-back-button icon-only"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <FaArrowLeft />
          </button>

          <div>
            <p className="bus-eyebrow">
              <FaBus /> Transport service
            </p>
            <h1>{title}</h1>
            <div className="bus-meta-row">
              <span>
                <FaMapMarkerAlt /> {busLocation}
              </span>
              <span>
                <FaRoute /> {routes.length || "No"} routes listed
              </span>
              <span>
                <FaMoneyBillWave /> {fareLabel}
              </span>
            </div>
          </div>
        </header>

        <section className="bus-hero-grid" aria-label={`${title} gallery`}>
          <div className="bus-main-image">
            {selectedImage ? (
              <img src={selectedImage} alt={`${title} main view`} />
            ) : (
              <div className="bus-image-placeholder">
                <FaBus />
                <span>Transport image coming soon</span>
              </div>
            )}
            <div className="bus-hero-overlay">
              <span>{operator}</span>
              <strong>{fareLabel}</strong>
            </div>
          </div>

          <div className="bus-thumbnail-grid">
            {[0, 1, 2, 3].map((index) => {
              const thumb = gallery[index + 1] || gallery[index];
              return thumb ? (
                <button
                  type="button"
                  key={`${thumb}-${index}`}
                  className={`bus-thumbnail ${
                    selectedImage === thumb ? "active" : ""
                  }`}
                  onClick={() => setSelectedImage(thumb)}
                  aria-label={`View ${title} image ${index + 1}`}
                >
                  <img src={thumb} alt={`${title} thumbnail ${index + 1}`} />
                </button>
              ) : (
                <div className="bus-thumbnail empty" key={index}>
                  <FaBus />
                </div>
              );
            })}
          </div>
        </section>

        <section className="bus-content-grid">
          <div>
            <article className="bus-section bus-review-panel">
              <span className="bus-icon-badge primary">
                <FaStar />
              </span>
              <div>
                <p>Service Rating</p>
                <div className="bus-stars">
                  <strong>{safeRating}</strong>
                  {[...Array(5)].map((_, index) => (
                    <FaStar
                      key={index}
                      className={index < safeRating ? "filled" : ""}
                    />
                  ))}
                </div>
              </div>
            </article>

            <article className="bus-section">
              <div className="bus-section-heading">
                <span>01</span>
                <h2>Service Information</h2>
              </div>
              <div className="bus-info-grid">
                <div className="bus-info-item">
                  <span className="bus-icon-badge cyan">
                    <FaMapMarkerAlt />
                  </span>
                  <div>
                    <p>Main Terminal</p>
                    <strong>{busLocation}</strong>
                  </div>
                </div>

                <div className="bus-info-item">
                  <span className="bus-icon-badge red">
                    <FaTicketAlt />
                  </span>
                  <div>
                    <p>Fare</p>
                    <strong>{fareLabel}</strong>
                  </div>
                </div>

                <div className="bus-info-item">
                  <span className="bus-icon-badge green">
                    <FaRoute />
                  </span>
                  <div>
                    <p>Route Coverage</p>
                    <strong>{routes.length || 0} main routes</strong>
                  </div>
                </div>

                <div className="bus-info-item">
                  <span className="bus-icon-badge charcoal">
                    <FaUserTie />
                  </span>
                  <div>
                    <p>Operator</p>
                    <strong>{operator}</strong>
                  </div>
                </div>
              </div>
            </article>

            <article className="bus-section">
              <div className="bus-section-heading">
                <span>02</span>
                <h2>About This Service</h2>
              </div>
              <p className="bus-description-text">{description}</p>
            </article>

            <article className="bus-section">
              <div className="bus-section-heading">
                <span>03</span>
                <h2>Available Routes</h2>
              </div>
              {routes.length ? (
                <div className="bus-route-grid">
                  {routes.map((route, index) => (
                    <div className="bus-route-chip" key={`${route}-${index}`}>
                      <FaRoute />
                      <span>{route}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="bus-empty-copy">
                  Route information has not been added yet.
                </p>
              )}
            </article>
          </div>

          <aside className="bus-side-panel">
            <article className="bus-schedule-card">
              <div className="bus-side-icon">
                <FaClock />
              </div>
              <h2>Schedule</h2>
              {schedule.length ? (
                <div className="bus-schedule-list">
                  {schedule.map((item, index) => (
                    <div className="bus-schedule-row" key={`${item}-${index}`}>
                      <span>{index + 1}</span>
                      <p>{item}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="bus-empty-copy">No schedule available.</p>
              )}
            </article>

            <article className="bus-contact-card">
              <h2>Plan Your Trip</h2>
              <p>
                Use the terminal location for directions, or contact the
                operator for availability and bookings.
              </p>

              <div className="bus-action-stack">
                <button type="button" onClick={openDirections}>
                  <FaMapMarkerAlt /> Open in Maps
                </button>
                <button type="button" onClick={callOperator}>
                  <FaPhone /> Call Operator
                </button>
                <button type="button" onClick={emailOperator}>
                  <FaEnvelope /> Email Operator
                </button>
              </div>
            </article>
          </aside>
        </section>
      </main>
      <section className="detail-extra-section">
        <VisitorTips title={title} category="Transport" />
      </section>

      <Footer />
    </div>
  );
};

export default BusDescription;
